import { fetchProfilePreview, fetchProfileHistory } from "./api.js";
import { buildPeriodAnalytics } from "./analytics.js";
import { detectWeightUnit } from "./format.js";

export function computePeriodWarning(workouts, pagination, periodDays) {
  if (!pagination?.budgetReached || !pagination?.hasMore) return null;
  if (!workouts?.length) return null;
  const periodStart = Date.now() - periodDays * 24 * 60 * 60 * 1000;
  const oldestMs = Math.min(...workouts.map((w) => new Date(w.startTime).getTime()));
  if (oldestMs <= periodStart) return null;
  return `This ${periodDays}-day analysis is limited to the latest ${pagination.maxWorkouts} imported workouts — some workouts earlier in this period were not fetched.`;
}

// `compareWith` names the base profile when this load is part of a comparison,
// and `compareSize` how many profiles that comparison then holds —
// without it a four-profile comparison reads as three separate pairs. Both only
// ever reach the server's usage log; nothing reads them back.
export async function loadProfile(username, periodDays, { onPreview, onProgress, compareWith, compareSize } = {}) {
  let preview;
  try {
    preview = await fetchProfilePreview(username, { compareWith, compareSize });
  } catch (err) {
    throw err;
  }

  onPreview?.(preview);

  // Callers stop here and surface their own message — there is no history to
  // fetch, so nothing past this point would have anything to work with.
  if (preview?.normalized?.profile?.isPrivate) {
    return { username, preview, dataStatus: "private" };
  }

  let history;
  try {
    history = await fetchProfileHistory(username, { compareWith, compareSize, onProgress });
  } catch (err) {
    throw err;
  }

  const profileWorkoutCount = preview?.normalized?.profile?.workoutCount || 0;
  const importFailed =
    history?.source?.state === "temp-account-auth-missing" ||
    (!history?.workouts?.length && profileWorkoutCount > 0);

  if (importFailed) {
    const message = history?.source?.state === "temp-account-auth-missing"
      ? "Workout history is temporarily unavailable. Please try again later."
      : "Couldn't load this profile's workout history. Please try again in a moment.";
    throw new Error(message);
  }

  let historyWarning = null;
  const pagination = history?.pagination;
  if (pagination?.budgetReached && pagination?.hasMore) {
    const profileTotal = preview?.normalized?.profile?.workoutCount;
    const importedMax = pagination?.maxWorkouts;
    historyWarning = `Only the latest ${importedMax} of ${profileTotal} workouts were imported.`;
  }

  const workouts = history?.workouts || [];
  const weightUnit = detectWeightUnit(workouts);
  const analytics = buildPeriodAnalytics(workouts, periodDays);
  const periodWarning = computePeriodWarning(workouts, pagination, periodDays);

  return {
    username,
    preview,
    history,
    workouts,
    weightUnit,
    analytics,
    dataStatus: "ready",
    historyWarning,
    periodWarning
  };
}
